import type { Caption } from '@remotion/captions';

import {
  isCaptionAsset,
  type CaptionAsset,
  type EditorStarterAsset,
} from '@/features/remotion-editor/editor/assets/assets';
import {
  isCaptionsItem,
  type CaptionsItem,
} from '@/features/remotion-editor/editor/items/captions/captions-item-type';
import {
  captionDisplayLanguageOf,
  withoutCaptionTranslation,
} from '@/features/remotion-editor/editor/state/actions/caption-translation-actions';
import {
  getConsistentCaptionSceneId,
  getPrimaryCaptionSpeaker,
  mergeBoundaryCaptionGroups,
  normalizeCaptionItemToTimeline,
} from '@/features/remotion-editor/editor/items/captions/caption-presentation-merge';
import { CAPTION_GAP_SPLIT_THRESHOLD_MS } from '@/features/remotion-editor/editor/items/captions/split-captions-by-gap';
import type { EditorStarterItem } from '@/features/remotion-editor/editor/items/item-type';
import type {
  EditorState,
  TrackType,
} from '@/features/remotion-editor/editor/state/types';
import {
  DtoProjectType,
  type DtoProjectType as DtoProjectTypeValue,
} from '@/shared/constants';

import { calculateCaptionAssetSize } from './asset-utils';
import { ensureEndGreaterThanStart, getCaptionRange } from './helpers';

interface CaptionMergeCandidate {
  previousItem: CaptionsItem;
  currentItem: CaptionsItem;
  previousAsset: CaptionAsset;
  currentAsset: CaptionAsset;
  previousCaptions: Caption[];
  currentCaptions: Caption[];
  previousAssetEndMs: number;
  currentAssetEndMs: number;
  sceneId: number | undefined;
  isCrossScenePresentationMerge: boolean;
}

interface CaptionMergeParams {
  state: EditorState;
  previousItemId: string;
  currentItemId: string;
  projectType?: DtoProjectTypeValue | null;
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const frameToMs = (frame: number, fps: number) =>
  Math.round((frame / fps) * 1000);

/**
 * Merges are lane-local. Imported overlapping captions are partitioned into
 * multiple caption tracks, so selecting the first main track would reject a
 * valid pair on a later lane and could never authorize cross-lane pairs.
 */
const getCaptionMergeOwner = (
  tracks: TrackType[],
  previousItemId: string,
  currentItemId: string,
): TrackType | undefined =>
  tracks.find(
    (track) => {
      // The presentation renderer only combines main-caption tracks. Imported
      // secondary lanes retain this track flag even though their items do not.
      if (track.metadata?.type !== 'caption' || !track.metadata.isMainTrack) return false;
      const currentItemIndex = track.items.indexOf(currentItemId);
      return (
        currentItemIndex > 0 &&
        track.items[currentItemIndex - 1] === previousItemId
      );
    },
  );

const resolveCaptionMergeCandidate = ({
  state,
  previousItemId,
  currentItemId,
  projectType,
}: CaptionMergeParams): CaptionMergeCandidate | null => {
  const { tracks, items, assets, fps } = state.undoableState;
  const track = getCaptionMergeOwner(
    tracks,
    previousItemId,
    currentItemId,
  );

  if (!track || track.locked) {
    return null;
  }

  const previousItem = items[previousItemId];
  const currentItem = items[currentItemId];
  if (!isCaptionsItem(previousItem) || !isCaptionsItem(currentItem)) {
    return null;
  }
  if (previousItem.id !== previousItemId || currentItem.id !== currentItemId) {
    return null;
  }

  const previousAsset = assets[previousItem.assetId];
  const currentAsset = assets[currentItem.assetId];
  if (!isCaptionAsset(previousAsset) || !isCaptionAsset(currentAsset)) {
    return null;
  }
  if (
    previousAsset.id !== previousItem.assetId ||
    currentAsset.id !== currentItem.assetId
  ) {
    return null;
  }

  // 표시 중인 언어가 다르면 병합하지 않는다 — 한 asset 안에 원문과 번역문이 섞이고
  // captionLanguage 라벨은 한쪽 값만 남아 실제 내용과 어긋난다 (design.md §7-4).
  if (
    captionDisplayLanguageOf(previousAsset) !==
    captionDisplayLanguageOf(currentAsset)
  ) {
    return null;
  }

  const isAgentVideoCaption = projectType === DtoProjectType.agentVideoCaption;
  let sceneId: number | undefined;
  let isCrossScenePresentationMerge = false;

  if (isAgentVideoCaption) {
    const previousSceneId = getConsistentCaptionSceneId(
      previousItem,
      previousAsset,
    );
    const currentSceneId = getConsistentCaptionSceneId(
      currentItem,
      currentAsset,
    );
    if (previousSceneId == null || currentSceneId == null) {
      return null;
    }
    sceneId = previousSceneId;
    isCrossScenePresentationMerge = previousSceneId !== currentSceneId;
  } else {
    // 공용 에디터의 기존 Backspace 계약: asset sceneId가 둘 다 없으면 병합 가능하다.
    if (previousAsset.sceneId !== currentAsset.sceneId) {
      return null;
    }
    sceneId = previousAsset.sceneId;
  }

  const safeFps = fps > 0 ? fps : 30;
  const previousTimelineData = normalizeCaptionItemToTimeline({
    item: previousItem,
    asset: previousAsset,
    fps: safeFps,
  });
  const currentTimelineData = normalizeCaptionItemToTimeline({
    item: currentItem,
    asset: currentAsset,
    fps: safeFps,
  });
  const previousBoundaryCaption =
    previousTimelineData.captions[previousTimelineData.captions.length - 1];
  const currentBoundaryCaption = currentTimelineData.captions[0];

  if (
    isAgentVideoCaption &&
    (!previousBoundaryCaption || !currentBoundaryCaption)
  ) {
    return null;
  }

  if (previousBoundaryCaption && currentBoundaryCaption) {
    const previousSpeaker = getPrimaryCaptionSpeaker(previousBoundaryCaption);
    const currentSpeaker = getPrimaryCaptionSpeaker(currentBoundaryCaption);
    if (
      isAgentVideoCaption &&
      previousSpeaker !== undefined &&
      currentSpeaker !== undefined &&
      previousSpeaker !== currentSpeaker
    ) {
      return null;
    }

    const gapMs = Math.max(
      0,
      currentBoundaryCaption.startMs - previousBoundaryCaption.endMs,
    );
    if (isAgentVideoCaption && gapMs >= CAPTION_GAP_SPLIT_THRESHOLD_MS) {
      return null;
    }

    // frame 단위 timeline 투영은 각 item을 독립적으로 반올림하므로 원본 1500ms
    // 경계가 1486ms처럼 짧아질 수 있다. 명시적 합치기 기준은 실제 음성 간격도
    // 함께 확인하여 임계값 경계가 프레임 반올림으로 열리지 않게 한다.
    if (isAgentVideoCaption) {
      const previousSourceBoundary = [...previousAsset.captions]
        .sort((left, right) => left.startMs - right.startMs)
        .at(-1);
      const currentSourceBoundary = [...currentAsset.captions].sort(
        (left, right) => left.startMs - right.startMs,
      )[0];
      if (previousSourceBoundary && currentSourceBoundary) {
        const sourceGapMs = Math.max(
          0,
          currentSourceBoundary.startMs - previousSourceBoundary.endMs,
        );
        if (sourceGapMs >= CAPTION_GAP_SPLIT_THRESHOLD_MS) {
          return null;
        }
      }
    }
  }

  if (
    previousTimelineData.captions.length === 0 &&
    currentTimelineData.captions.length === 0
  ) {
    return null;
  }

  return {
    previousItem,
    currentItem,
    previousAsset,
    currentAsset,
    previousCaptions: previousTimelineData.captions,
    currentCaptions: currentTimelineData.captions,
    previousAssetEndMs: previousTimelineData.assetEndMs,
    currentAssetEndMs: currentTimelineData.assetEndMs,
    sceneId,
    isCrossScenePresentationMerge,
  };
};

const createMergedCaptionClipId = (
  assets: Record<string, EditorStarterAsset>,
  items: Record<string, EditorStarterItem>,
): number => {
  const usedClipIds = new Set<number>();
  let maxClipId = 0;

  const collectClipId = (candidate: unknown) => {
    if (!isFiniteNumber(candidate) || !Number.isSafeInteger(candidate)) {
      return;
    }

    usedClipIds.add(candidate);
    maxClipId = Math.max(maxClipId, candidate);
  };

  Object.values(assets).forEach((asset) => {
    if ('clipId' in asset) {
      collectClipId(asset.clipId);
    }
  });

  Object.values(items).forEach((item) => {
    if (
      'renderMeta' in item &&
      item.renderMeta != null &&
      typeof item.renderMeta === 'object' &&
      'id' in item.renderMeta
    ) {
      collectClipId(item.renderMeta.id);
    }
  });

  if (maxClipId < Number.MAX_SAFE_INTEGER) {
    return maxClipId + 1;
  }

  let candidate = 1;
  while (usedClipIds.has(candidate)) {
    candidate += 1;
  }
  return candidate;
};

const createMergedCaptionAssetId = ({
  assets,
  previousItemId,
  currentItemId,
}: {
  assets: Record<string, EditorStarterAsset>;
  previousItemId: string;
  currentItemId: string;
}) => {
  const baseId = `caption-merge-${previousItemId}-${currentItemId}`;
  let candidateId = baseId;
  let suffix = 2;

  while (assets[candidateId]) {
    candidateId = `${baseId}-${suffix}`;
    suffix += 1;
  }

  return candidateId;
};

const getReferencedAssetIds = (
  items: Record<string, EditorStarterItem>,
): Set<string> => {
  const referencedAssetIds = new Set<string>();

  Object.values(items).forEach((item) => {
    if ('assetId' in item && typeof item.assetId === 'string') {
      referencedAssetIds.add(item.assetId);
    }
  });

  return referencedAssetIds;
};

export const isCaptionPresentationMergeBoundary = ({
  state,
  previousItemId,
  currentItemId,
  projectType,
}: CaptionMergeParams): boolean => {
  if (projectType !== DtoProjectType.agentVideoCaption) {
    return false;
  }

  const track = getCaptionMergeOwner(
    state.undoableState.tracks,
    previousItemId,
    currentItemId,
  );
  if (
    !track ||
    track.locked
  ) {
    return false;
  }

  const previousItem = state.undoableState.items[previousItemId];
  return (
    isCaptionsItem(previousItem) &&
    previousItem.captionPresentationNextItemId === currentItemId
  );
};

export const canMergeCaptionItems = (params: CaptionMergeParams): boolean => {
  if (isCaptionPresentationMergeBoundary(params)) {
    return false;
  }
  return resolveCaptionMergeCandidate(params) !== null;
};

export const unmergeCaptionPresentationBoundary = ({
  state,
  previousItemId,
  currentItemId,
  projectType,
}: CaptionMergeParams): EditorState => {
  if (
    !isCaptionPresentationMergeBoundary({
      state,
      previousItemId,
      currentItemId,
      projectType,
    })
  ) {
    return state;
  }

  const previousItem = state.undoableState.items[previousItemId];
  if (!isCaptionsItem(previousItem)) {
    return state;
  }

  const nextPreviousItem: CaptionsItem = { ...previousItem };
  delete nextPreviousItem.captionPresentationNextItemId;

  return {
    ...state,
    undoableState: {
      ...state.undoableState,
      items: {
        ...state.undoableState.items,
        [previousItemId]: nextPreviousItem,
      },
    },
  };
};

/**
 * 같은 장면의 인접 자막은 기존처럼 물리 병합하고, 다른 장면은 원본 item/asset을
 * 보존한 채 표시 경계만 연결한다. 모든 eligibility를 action 내부에서 다시 검증한다.
 */
export const mergeCaptionItems = ({
  state,
  previousItemId,
  currentItemId,
  projectType,
}: CaptionMergeParams): EditorState => {
  const candidate = resolveCaptionMergeCandidate({
    state,
    previousItemId,
    currentItemId,
    projectType,
  });
  if (!candidate) {
    return state;
  }

  const {
    previousItem,
    currentItem,
    previousAsset,
    currentAsset,
    previousCaptions,
    currentCaptions,
    previousAssetEndMs,
    currentAssetEndMs,
    sceneId,
    isCrossScenePresentationMerge,
  } = candidate;
  const { tracks, items, assets } = state.undoableState;

  if (isCrossScenePresentationMerge) {
    if (previousItem.captionPresentationNextItemId === currentItem.id) {
      return state;
    }

    return {
      ...state,
      selectedItems: [previousItem.id],
      undoableState: {
        ...state.undoableState,
        items: {
          ...items,
          [previousItem.id]: {
            ...previousItem,
            captionPresentationNextItemId: currentItem.id,
            hasUserDefinedPresentation: true,
          },
          [currentItem.id]: {
            ...currentItem,
            hasUserDefinedPresentation: true,
          },
        },
      },
    };
  }

  const safeFps = state.undoableState.fps > 0 ? state.undoableState.fps : 30;
  const mergedCaptions = mergeBoundaryCaptionGroups(
    previousCaptions,
    currentCaptions,
  );
  const mergedRange = getCaptionRange(mergedCaptions);
  const mergedFrom = Math.min(previousItem.from, currentItem.from);
  const previousEndFrame = previousItem.from + previousItem.durationInFrames;
  const currentEndFrame = currentItem.from + currentItem.durationInFrames;
  const requestedEndMs = Math.max(
    mergedRange.end,
    previousAssetEndMs,
    currentAssetEndMs,
  );
  const mergedEndFrame = Math.max(
    mergedFrom + 1,
    previousEndFrame,
    currentEndFrame,
    Math.ceil((requestedEndMs / 1000) * safeFps),
  );
  const mergedVideoStart = frameToMs(mergedFrom, safeFps);
  const mergedVideoEnd = ensureEndGreaterThanStart(
    mergedVideoStart,
    requestedEndMs,
  );
  const mergedAssetId = createMergedCaptionAssetId({
    assets,
    previousItemId,
    currentItemId,
  });
  const mergedClipId = createMergedCaptionClipId(assets, items);
  const mergedAsset: CaptionAsset = {
    ...withoutCaptionTranslation(previousAsset),
    id: mergedAssetId,
    clipId: mergedClipId,
    filename: `${mergedAssetId}.json`,
    remoteUrl: null,
    remoteFileKey: null,
    captions: mergedCaptions,
    size: calculateCaptionAssetSize(mergedCaptions),
    videoStart: mergedVideoStart,
    videoEnd: mergedVideoEnd,
    ...(sceneId === undefined ? {} : { sceneId }),
  };
  const baseRenderMeta = previousItem.renderMeta ?? currentItem.renderMeta;
  const mergedItem: CaptionsItem = {
    ...previousItem,
    assetId: mergedAsset.id,
    from: mergedFrom,
    durationInFrames: mergedEndFrame - mergedFrom,
    captionStartInSeconds: mergedVideoStart / 1000,
    captionPresentationNextItemId: currentItem.captionPresentationNextItemId,
    hasUserDefinedPresentation: true,
    ...(baseRenderMeta
      ? {
          renderMeta: {
            ...baseRenderMeta,
            id: mergedClipId,
            videoStart: mergedVideoStart,
            videoEnd: mergedVideoEnd,
            ...(sceneId === undefined ? {} : { sceneId }),
          },
        }
      : {}),
  };

  const nextItems = { ...items };
  delete nextItems[currentItem.id];
  nextItems[previousItem.id] = mergedItem;

  const nextTracks = tracks.map((candidateTrack) => {
    if (!candidateTrack.items.includes(currentItem.id)) {
      return candidateTrack;
    }

    return {
      ...candidateTrack,
      items: candidateTrack.items.filter((itemId) => itemId !== currentItem.id),
    };
  });

  const nextAssets: Record<string, EditorStarterAsset> = {
    ...assets,
    [mergedAsset.id]: mergedAsset,
  };
  const referencedAssetIds = getReferencedAssetIds(nextItems);
  const sourceAssets = new Map(
    [previousAsset, currentAsset].map((asset) => [asset.id, asset]),
  );
  const orphanedSourceAssets = [...sourceAssets.values()].filter(
    (asset) => !referencedAssetIds.has(asset.id),
  );

  orphanedSourceAssets.forEach((asset) => {
    delete nextAssets[asset.id];
  });

  const nextDeletedAssets = [...state.undoableState.deletedAssets];
  orphanedSourceAssets.forEach((asset) => {
    if (
      nextDeletedAssets.some(
        (deletedAsset) => deletedAsset.assetId === asset.id,
      )
    ) {
      return;
    }

    nextDeletedAssets.push({
      assetId: asset.id,
      remoteUrl: asset.remoteUrl,
      remoteFileKey: asset.remoteFileKey,
      statusAtDeletion: state.assetStatus[asset.id] ?? { type: 'uploaded' },
    });
  });

  return {
    ...state,
    selectedItems:
      projectType === DtoProjectType.agentVideoCaption
        ? [previousItem.id]
        : state.selectedItems,
    undoableState: {
      ...state.undoableState,
      tracks: nextTracks,
      items: nextItems,
      assets: nextAssets,
      deletedAssets: nextDeletedAssets,
    },
  };
};
